import {ArrayGroupOptions, BaseGroup, Field, Reference, ValueDefinition} from '@/model/types';
import {ACTION_TYPES} from '@/model/types/constants';
import {
  AccessMap,
  DependencyMap,
  DependencyRegistrer,
  GroupAccessRecord,
  ModelArrayGroup,
  ModelBaseGroup,
  SectionAccessRecord,
  VisibilitySet,
} from '../types';
import {recursiveReplaceIndex} from '../utils/object';
import {mapToModelBaseGroup} from './group';

export type ModelArrayRowsFactory = Pick<ModelArrayGroup, 'rows' | 'row' | 'rowCount'>;

const removeVisibilityOfRowsBeyondCount = (visibilitySet: VisibilitySet, fields: Field[], rowCount: number): void => {
  fields
    .filter(field => field.id.includes('#'))
    .flatMap(field => {
      const query = field.id.split('#')[0] + '#';
      return Array.from(visibilitySet.keys()).filter(
        key => key.startsWith(query) && Number(key.split('#')[1]) >= rowCount,
      );
    })
    .forEach(key => visibilitySet.delete(key));
};

export const createArrayRowsFactory = (
  sectionId: string,
  groupId: string,
  options: ArrayGroupOptions | undefined,
  fields: Field[],
  baseGroup: Omit<BaseGroup, 'id' | 'fields'>,
  values: ValueDefinition[],
  dependencyMap: DependencyMap,
  accessMap: AccessMap,
  dependencyRegistrer: DependencyRegistrer,
): ModelArrayRowsFactory => {
  const sectionIndex = (accessMap.get(sectionId) as SectionAccessRecord).sectionIndex;
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const groupIndex = (accessMap.get(groupId)! as GroupAccessRecord).groupIndex;

  let repeat: Reference;
  if (options?.$repeat !== undefined) {
    repeat = options.$repeat;
  } else {
    repeat = {$ref: `_${groupId}[count]`};
    accessMap.set(`_${groupId}[count]`, {sectionIndex, groupIndex});
  }

  dependencyRegistrer('visibility', repeat.$ref);

  const rowModels = new Map<number, ModelBaseGroup>();

  const createRow = (index: number): ModelBaseGroup => {
    const rowGroupId = `${groupId}#${index}`;
    accessMap.set(rowGroupId, {sectionIndex, groupIndex});

    const mappedFields = recursiveReplaceIndex(fields, index);

    const setFieldsRefEvent = {
      action: {
        type: ACTION_TYPES.SET,
        field: `_${rowGroupId}[fields]`,
        value: recursiveReplaceIndex(mappedFields.map(field => field.id).join(','), index),
      },
    };

    return mapToModelBaseGroup(
      sectionId,
      {
        ...recursiveReplaceIndex(baseGroup, index),
        id: rowGroupId,
        onInit: [setFieldsRefEvent],
        onMount: [...(baseGroup.onMount ?? []), setFieldsRefEvent],
        fields: mappedFields,
      },
      values,
      dependencyMap,
      accessMap,
    );
  };

  const row = (index: number): ModelBaseGroup => {
    const cached = rowModels.get(index);
    if (cached) return cached;

    const rowModel = createRow(index);
    rowModels.set(index, rowModel);
    return rowModel;
  };

  const rowCount: ModelArrayGroup['rowCount'] = valueResolver => Number(valueResolver(repeat));

  const rows: ModelArrayGroup['rows'] = (valueResolver, visibilitySet) => {
    const repeatCount = rowCount(valueResolver, visibilitySet);

    removeVisibilityOfRowsBeyondCount(visibilitySet, fields, repeatCount);

    return Array.from({length: repeatCount}).map((_, index) => row(index));
  };

  return {rows, row, rowCount};
};
